import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../common/page_component/index.js';
import Loader from '../common/Loader.js';
import DateFilterDropdown from './DateFilterDropdown.js';
import analyticsService from '../../services/AnalyticsService';
import { Package, IndianRupee, TrendingUp, Filter, Eye, ArrowLeft, CreditCard, Wallet } from 'lucide-react';
import Button from '../common/Button.js';
import { useTranslation } from 'react-i18next';
import {
  AnalyticsStatCard,
  AnalyticsTable,
  analyticsStatsGrid4Class,
  analyticsStatsGrid2Class,
  analyticsPaymentModesGridClass,
  analyticsChargesGridClass,
  analyticsSectionClass,
  useAnalyticsCompanyScope,
  buildAnalyticsParams,
} from './common/index.js';
import { formatCurrencyWhole as formatCurrency } from '../../utils/formatCurrency.js';


const ShipmentAnalytics = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState('current_month');
  const [customDates, setCustomDates] = useState({ from_date: '', to_date: '' });
  const [additionalFilters, setAdditionalFilters] = useState({
    user_id: '',
    status: '',
    vehicle_id: '',
    payment_mode: '',
    location: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const {
    currentUser,
    companies,
    selectedCompany,
    handleCompanyChange,
    ready,
  } = useAnalyticsCompanyScope({ filter, customDates });

  useEffect(() => {
    if (ready) fetchData();
  }, [filter, selectedCompany, ready]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = buildAnalyticsParams({
        filter,
        customDates,
        selectedCompany,
        currentUser,
        extra: Object.fromEntries(
          Object.entries(additionalFilters).filter(([_, v]) => v !== '')
        ),
      });
      
      const response = await analyticsService.getShipmentAnalytics(params);
      setData(response);
    } catch (error) {
      console.error('Error fetching shipment analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilters = () => {
    fetchData();
    setShowFilters(false);
  };

  const StatusBreakdown = () => {
    if (!data?.breakdown?.by_status) return null;
    
    return (
      <div className={`${analyticsSectionClass} min-w-0 h-full`}>
        <h3 className="text-sm sm:text-base lg:text-lg font-semibold text-gray-800 mb-2 sm:mb-3 md:mb-4">Shipments by Status</h3>
        <div className="space-y-2 sm:space-y-3">
          {Object.entries(data.breakdown.by_status).map(([status, stats]) => (
            <div key={status} className="flex items-start justify-between gap-2 p-2 sm:p-3 bg-gray-50 rounded-xl min-w-0">
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-800 text-xs sm:text-sm truncate">{status}</p>
                <p className="text-[10px] sm:text-sm text-gray-600">{stats.count} shipments</p>
              </div>
              <div className="text-right shrink-0">
                <p className="font-semibold text-gray-800 text-xs sm:text-sm">{formatCurrency(stats.revenue)}</p>
                <p className="text-[10px] sm:text-xs text-green-600">Paid: {formatCurrency(stats.paid)}</p>
                <p className="text-[10px] sm:text-xs text-orange-600">Bal: {formatCurrency(stats.balance)}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const PaymentModeBreakdown = () => {
    if (!data?.breakdown?.by_payment_mode) return null;
    
    return (
      <div className={`${analyticsSectionClass} min-w-0 h-full`}>
        <h3 className="text-sm sm:text-base lg:text-lg font-semibold text-gray-800 mb-2 sm:mb-3 md:mb-4">Shipments by Payment Mode</h3>
        <div className="space-y-2 sm:space-y-3">
          {Object.entries(data.breakdown.by_payment_mode).map(([mode, stats]) => (
            <div key={mode} className="flex items-start justify-between gap-2 p-2 sm:p-3 bg-gray-50 rounded-xl min-w-0">
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-800 text-xs sm:text-sm capitalize truncate">{mode.replace('_', ' ')}</p>
                <p className="text-[10px] sm:text-sm text-gray-600">{stats.count} shipments</p>
              </div>
              <div className="text-right shrink-0">
                <p className="font-semibold text-gray-800 text-xs sm:text-sm">{formatCurrency(stats.revenue)}</p>
                <p className="text-[10px] sm:text-xs text-green-600">Paid: {formatCurrency(stats.paid)}</p>
                <p className="text-[10px] sm:text-xs text-orange-600">Bal: {formatCurrency(stats.balance)}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const PaymentHistoryBreakdown = () => {
    if (!data?.breakdown?.payment_history_by_mode) return null;
    
    const paymentModes = [
      { key: 'cash', label: 'Cash', icon: Wallet, color: 'bg-blue-50 text-blue-600' },
      { key: 'upi', label: 'UPI', icon: CreditCard, color: 'bg-blue-50 text-blue-600' },
      { key: 'cheque', label: 'Cheque', icon: CreditCard, color: 'bg-blue-50 text-blue-600' },
      { key: 'bank_transfer', label: 'Bank Transfer', icon: CreditCard, color: 'bg-blue-50 text-blue-600' }
    ];

    return (
      <div className={analyticsSectionClass}>
        <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4">Payment History by Mode</h3>
        <p className="text-sm text-gray-600 mb-4">Actual payments received through different payment methods</p>
        
        <div className={analyticsPaymentModesGridClass}>
          {paymentModes.map(({ key, label, icon: Icon, color }) => {
            const modeData = data.breakdown.payment_history_by_mode[key] || { payment_count: 0, total_amount: 0 };
            return (
              <div key={key} className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-4 border border-gray-200">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-gray-800">{label}</h4>
                  <div className={`p-2 rounded-xl ${color}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                </div>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] sm:text-xs text-gray-600">Payment Count</p>
                    <p className="text-base sm:text-xl font-bold text-gray-800">{modeData.payment_count}</p>
                  </div>
                  <div className="min-w-0 flex-1 text-right">
                    <p className="text-[10px] sm:text-xs text-gray-600">Total Amount</p>
                    <p className="text-sm sm:text-base lg:text-lg font-semibold text-green-600 whitespace-nowrap">
                      {formatCurrency(modeData.total_amount)}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Summary */}
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <p className="text-sm text-gray-600">Total Payments Recorded</p>
              <p className="text-xl font-bold text-gray-800">
                {Object.values(data.breakdown.payment_history_by_mode).reduce((sum, mode) => sum + mode.payment_count, 0)}
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-600">Total Amount Received</p>
              <p className="text-xl font-bold text-green-600">
                {formatCurrency(
                  Object.values(data.breakdown.payment_history_by_mode).reduce((sum, mode) => sum + mode.total_amount, 0)
                )}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const UserPerformanceTable = () => {
    if (!data?.breakdown?.by_user) return null;

    const columns = [
      { header: 'User', align: 'left' },
      { header: 'City', align: 'left' },
      { header: t('pages.shipment.title'), align: 'right' },
      { header: 'Revenue', align: 'right' },
      { header: 'Collected', align: 'right', cellClassName: 'text-green-600' },
      { header: 'Balance', align: 'right', cellClassName: 'text-orange-600' },
      { header: 'Action', align: 'center' },
    ];

    const rows = data.breakdown.by_user.map((user) => ({
      key: user.user_id,
      cells: [
        user.name,
        user.city,
        user.shipment_count,
        formatCurrency(user.total_revenue),
        formatCurrency(user.paid),
        formatCurrency(user.balance),
        <button
          key="action"
          type="button"
          onClick={() => navigate(`/users/view/${user.user_id}`)}
          className="text-blue-600 hover:text-blue-800"
        >
          <Eye className="w-4 h-4 inline" />
        </button>,
      ],
    }));

    return (
      <AnalyticsTable
        title="Performance by User"
        columns={columns}
        rows={rows}
        tableMinWidth="720px"
      />
    );
  };

  const VehicleBreakdown = () => {
    if (!data?.breakdown?.by_vehicle) return null;

    return (
      <div className={analyticsSectionClass}>
        <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4">Shipments by Vehicle</h3>
        <div className={analyticsStatsGrid2Class}>
          {data.breakdown.by_vehicle.map((vehicle) => (
            <div key={vehicle.vehicle_id || 'unassigned'} className="p-4 bg-gray-50 rounded-xl">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold text-gray-800">{vehicle.vehicle_number}</h4>
                <span className="text-sm text-gray-600">{vehicle.shipment_count} shipments</span>
              </div>
              <p className="text-sm text-gray-600 mb-2">{vehicle.vehicle_name}</p>
              <p className="text-lg font-bold text-gray-800">{formatCurrency(vehicle.total_revenue)}</p>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const LocationBreakdown = () => {
    if (!data?.breakdown?.by_location) return null;
    const locations = data.breakdown.by_location.sender_locations || [];
    const grandTotal = data.breakdown.by_location.grand_total;

    const columns = [
      { header: 'Sender Location', align: 'left' },
      { header: t('pages.shipment.title'), align: 'right' },
      { header: 'Total', align: 'right' },
      { header: 'Paid', align: 'right', cellClassName: 'text-green-600' },
      { header: 'To Pay', align: 'right', cellClassName: 'text-orange-600' },
    ];

    const rows = locations.map((loc, idx) => ({
      key: idx,
      cells: [
        loc.location,
        loc.count,
        formatCurrency(loc.total_revenue),
        formatCurrency(loc.paid),
        formatCurrency(loc.to_pay ?? loc.balance),
      ],
    }));

    const footerRow = grandTotal
      ? {
          cells: [
            'Grand Total',
            grandTotal.count,
            formatCurrency(grandTotal.total_revenue),
            formatCurrency(grandTotal.paid),
            formatCurrency(grandTotal.to_pay ?? grandTotal.balance),
          ],
        }
      : null;

    return (
      <AnalyticsTable
        title="Sender Location Breakdown"
        columns={columns}
        rows={rows}
        footerRow={footerRow}
        tableMinWidth="560px"
      />
    );
  };

  if (loading) {
    return (
      <DashboardLayout>
        <Loader fullScreen={false} size="default" message="Loading Shipment Analytics..." />
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout>
              <div className="max-w-7xl mx-auto">
                {/* Header with Date Filter */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-3">
                  <div>
                    <h1 className="text-2xl sm:text-xl font-semibold text-secondary">{t('pages.analytics.shipments')}</h1>
                    <p className="text-sm text-gray-600">Comprehensive shipment performance metrics</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <DateFilterDropdown
                      filter={filter}
                      setFilter={setFilter}
                      customDates={customDates}
                      setCustomDates={setCustomDates}
                      onApply={fetchData}
                      dateRange={data?.filter}
                      currentUser={currentUser}
                      companies={companies}
                      selectedCompany={selectedCompany}
                      onCompanyChange={handleCompanyChange}
                      showCompanyFilter
                    />
                    <Button variant="primary" icon={ArrowLeft} onClick={() => navigate('/analytics')}>Back</Button>
                  </div>
                </div>

                {/* Additional Filters */}
                <div className="bg-white rounded-xl shadow-md p-4 mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-gray-800">Additional Filters</h3>
                    <button
                      onClick={() => setShowFilters(!showFilters)}
                      className="flex items-center text-sm text-blue-600 hover:text-blue-800"
                    >
                      <Filter className="w-4 h-4 mr-1" />
                      {showFilters ? 'Hide' : 'Show'} Filters
                    </button>
                  </div>
                  
                  {showFilters && (
                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 pt-3 border-t border-gray-200">
                      <input
                        type="text"
                        placeholder="User ID"
                        value={additionalFilters.user_id}
                        onChange={(e) => setAdditionalFilters({ ...additionalFilters, user_id: e.target.value })}
                        className="px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 text-sm"
                      />
                      <input
                        type="text"
                        placeholder="Status"
                        value={additionalFilters.status}
                        onChange={(e) => setAdditionalFilters({ ...additionalFilters, status: e.target.value })}
                        className="px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 text-sm"
                      />
                      <input
                        type="text"
                        placeholder="Location"
                        value={additionalFilters.location}
                        onChange={(e) => setAdditionalFilters({ ...additionalFilters, location: e.target.value })}
                        className="px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 text-sm"
                      />
                      <button
                        onClick={handleApplyFilters}
                        className="lg:col-span-3 px-4 py-2 bg-sky-50 text-sky-700 border border-sky-200 rounded-xl hover:bg-sky-100 transition-colors text-sm font-medium"
                      >
                        Apply Filters
                      </button>
                    </div>
                  )}
                </div>

                {/* Summary Cards */}
                {data && (
                  <>
                    <div className={`${analyticsStatsGrid4Class} mb-6`}>
                      <AnalyticsStatCard
                        title="Total Shipments"
                        value={data.summary.total_shipments}
                        subtitle="All shipments"
                        icon={Package}
                        color="bg-blue-100 text-blue-600"
                      />
                      <AnalyticsStatCard
                        title="Total Revenue"
                        value={formatCurrency(data.summary.total_revenue)}
                        subtitle="Total amount"
                        icon={IndianRupee}
                        color="bg-emerald-100 text-emerald-600"
                      />
                      <AnalyticsStatCard
                        title="Paid Amount"
                        value={formatCurrency(data.summary.paid_amount)}
                        subtitle="Collected"
                        icon={TrendingUp}
                        color="bg-teal-100 text-teal-600"
                      />
                      <AnalyticsStatCard
                        title="Balance"
                        value={formatCurrency(data.summary.balance_amount)}
                        subtitle="Pending collection"
                        icon={IndianRupee}
                        color="bg-orange-100 text-orange-600"
                      />
                    </div>

                    {/* Charges Breakdown */}
                    <div className={`${analyticsSectionClass} mb-6`}>
                      <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4">Charges Breakdown</h3>
                      <div className={analyticsChargesGridClass}>
                        <div className="text-center">
                          <p className="text-sm text-gray-600">Base Charges</p>
                          <p className="text-lg font-bold text-gray-800">{formatCurrency(data.summary.charges_breakdown.base_charges)}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-sm text-gray-600">Labour</p>
                          <p className="text-lg font-bold text-gray-800">{formatCurrency(data.summary.charges_breakdown.labour_charges)}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-sm text-gray-600">Late Charges</p>
                          <p className="text-lg font-bold text-gray-800">{formatCurrency(data.summary.charges_breakdown.late_charges)}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-sm text-gray-600">Delivery</p>
                          <p className="text-lg font-bold text-gray-800">{formatCurrency(data.summary.charges_breakdown.delivery_charges)}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-sm text-gray-600">Discounts</p>
                          <p className="text-lg font-bold text-red-600">-{formatCurrency(data.summary.charges_breakdown.total_discounts)}</p>
                        </div>
                      </div>
                    </div>

                    {/* Breakdowns */}
                    <div className="space-y-6">
                      {/* NEW: Payment History Breakdown */}
                      <PaymentHistoryBreakdown />
                      
                      {/* Always side-by-side on mobile, iPad, and desktop */}
                      <div className="grid grid-cols-2 gap-2 sm:gap-3 md:gap-4 lg:gap-6 items-start">
                        <StatusBreakdown />
                        <PaymentModeBreakdown />
                      </div>
                      <UserPerformanceTable />
                      <VehicleBreakdown />
                      <LocationBreakdown />
                    </div>
                  </>
                )}
              </div>
    </DashboardLayout>
  );
};

export default ShipmentAnalytics;